import { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useIntl } from "react-intl";
import Section from "../../common/section";
import {
  learningMilestoneCountSummaryRequest,
  learningMilestoneItemSummaryRequest,
  learningMilestoneUsersRequest,
  setLearningMilestoneQuery,
  learningMilestoneUserExcelRequest,
} from "../../../store/slices/reportSlice";
import LearningStatusSummary from "./widgets/LearningStatusSummary";
import LearningItemSummary from "./widgets/LearningItemSummary";
import LearningUsersSummary from "./widgets/LearningUsersSummary";
import DropdownButton from "../../common/dropDownButton";

const LearningMilestoneReport = ({
  filters = [],
  additionalQuery = {},
  learningMilestones,
  loadLearningMilestoneCountSummary,
  loadLearningMilestoneItemSummary,
  loadLearningMilestoneUsers,
  setLearningMilestoneQuery,
  loadLearningMilestoneUserExcel,
}) => {
  const intl = useIntl();

  const fetchReports = useCallback(() => {
    const payloadFilters = filters
      .filter((item) => item.values.length > 0)
      .map((item) => ({
        id: item.id,
        values: item.values,
        isDynamicField:
          item.id !== "Designation" &&
          item.id !== "Department" &&
          item.id !== "ManagerId",
      }));

    loadLearningMilestoneCountSummary({
      query: additionalQuery,
      filters: payloadFilters,
    });
    loadLearningMilestoneItemSummary({
      query: additionalQuery,
      filters: payloadFilters,
    });
  }, [
    filters,
    loadLearningMilestoneCountSummary,
    loadLearningMilestoneItemSummary,
    setLearningMilestoneQuery,
    additionalQuery,
  ]);

  useEffect(() => {
    if (learningMilestones.query.pageNo !== 1) {
      setLearningMilestoneQuery({
        ...learningMilestones.query,
        pageNo: 1,
      });
    }
  }, [filters]);

  const fetchUserList = useCallback(() => {
    const payloadFilters = filters
      .filter((item) => item.values.length > 0)
      .map((item) => ({
        id: item.id,
        values: item.values,
        isDynamicField:
          item.id !== "Designation" &&
          item.id !== "Department" &&
          item.id !== "ManagerId",
      }));
    loadLearningMilestoneUsers({
      query: {
        ...learningMilestones.query,
        ...additionalQuery,
      },
      filters: payloadFilters,
    });
  }, [learningMilestones.query, filters, additionalQuery]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  useEffect(() => {
    fetchUserList();
  }, [fetchUserList]);

  const exportReport = (fileType = "excel") => {
    loadLearningMilestoneUserExcel({
      fileType,
      query: {
        ...learningMilestones.query,
        ...additionalQuery,
      },
      filters: filters
        .filter((item) => item.values.length > 0)
        .map((item) => ({
          id: item.id,
          values: item.values,
          isDynamicField:
            item.id !== "Designation" &&
            item.id !== "Department" &&
            item.id !== "ManagerId",
        })),
    });
  };

  return (
    <div className="grid">
      <div className="col-12 md:col-4">
        <Section title="summary">
          <LearningStatusSummary
            loading={learningMilestones?.countSummary === null}
            data={learningMilestones?.countSummary || []}
          />
        </Section>
      </div>
      <div className="col-12 md:col-8">
        <Section title="by_learning_Milestones">
          <LearningItemSummary
            loading={learningMilestones?.itemSummary === null}
            data={learningMilestones?.itemSummary || []}
          />
        </Section>
      </div>
      <div className="col-12">
        <Section title="details">
          <div
            className="flex justify-content-end"
            style={{ marginBottom: 10 }}
          >
            <DropdownButton
              icon="pi pi-download"
              label={intl.formatMessage({ id: "export" })}
              items={[
                {
                  label: intl.formatMessage({ id: "excel" }),
                  command: () => exportReport("excel"),
                },
                {
                  label: intl.formatMessage({ id: "csv" }),
                  command: () => exportReport("csv"),
                },
                {
                  label: intl.formatMessage({ id: "pdf" }),
                  command: () => exportReport("pdf"),
                },
              ]}
            />
          </div>
          <LearningUsersSummary
            statusLabel="learning_milestone_status"
            learningName="learning_milestone"
            showLearningProgramDetails={true}
            loading={learningMilestones?.list?.list === null}
            data={learningMilestones?.list?.list || []}
            totalRecords={learningMilestones?.list?.totalCount || 0}
            query={learningMilestones.query || {}}
            setQuery={setLearningMilestoneQuery}
          />
        </Section>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  learningMilestones: state.reports.learningMilestones || {},
});

const mapDispatchToProps = (dispatch) => ({
  loadLearningMilestoneCountSummary: (data) =>
    dispatch(learningMilestoneCountSummaryRequest(data)),
  loadLearningMilestoneItemSummary: (data) =>
    dispatch(learningMilestoneItemSummaryRequest(data)),
  loadLearningMilestoneUsers: (data) =>
    dispatch(learningMilestoneUsersRequest(data)),
  setLearningMilestoneQuery: (data) =>
    dispatch(setLearningMilestoneQuery(data)),
  loadLearningMilestoneUserExcel: (data) =>
    dispatch(learningMilestoneUserExcelRequest(data)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LearningMilestoneReport);
